import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { uploadProfileImage, updateUserProfileData, resizeImage } from '@/lib/profile';
import { User, Mail, Upload, Check, Loader2 } from 'lucide-react';

export function ProfileEditCard() {
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [photoURL, setPhotoURL] = useState(user?.photoURL || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // プレビュー表示
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      // 画像をリサイズ（800x800まで）
      const resizedBlob = await resizeImage(file, 800, 800);
      const resizedFile = new File([resizedBlob], file.name, { type: file.type });

      // アップロード
      const url = await uploadProfileImage(user.uid, resizedFile);
      setPhotoURL(url);
      setPreviewImage(null);
    } catch (error) {
      console.error('Image upload error:', error);
      alert('画像のアップロードに失敗しました');
      setPreviewImage(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    setSuccess(false);

    try {
      await updateUserProfileData(user.uid, {
        displayName,
        photoURL,
      } as any);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

      // ページをリロードして変更を反映
      window.location.reload();
    } catch (error) {
      console.error('Profile update error:', error);
      alert('プロフィールの更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <User className="w-6 h-6 text-blue-600" />
        プロフィール情報
      </h2>

      <div className="space-y-6">
        {/* プロフィール画像 */}
        <div className="flex flex-col items-center">
          <div className="relative">
            <img
              src={previewImage || photoURL || '/images/sidepocket_symbol.png'}
              alt="Profile"
              className="w-32 h-32 rounded-full object-cover border-4 border-blue-100 shadow-lg"
            />
            {uploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
          </div>
          <label className="mt-4 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md cursor-pointer flex items-center gap-2">
            <Upload className="w-4 h-4" />
            画像を変更
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              disabled={uploading}
            />
          </label>
          <p className="text-xs text-gray-500 mt-2">推奨: 正方形、800x800px以下</p>
        </div>

        {/* 表示名 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            表示名
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="山田 太郎"
          />
        </div>

        {/* メールアドレス（読み取り専用） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            メールアドレス
          </label>
          <input
            type="email"
            value={user?.email || ''}
            readOnly
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 mt-1">メールアドレスは変更できません</p>
        </div>

        {/* 保存ボタン */}
        <div className="flex justify-end gap-3">
          {success && (
            <div className="flex items-center gap-2 text-green-600 font-medium">
              <Check className="w-5 h-5" />
              <span>保存しました</span>
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                保存中...
              </>
            ) : (
              '保存する'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
